import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { supplierAPI, productAPI, bulkAPI, whatsappAPI, paymentAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';

const fmtNum = n => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtDate = d => formatDateDMY(d, '—');

/* ── WhatsApp Send Button (stateful) ────────── */
const WhatsAppButton = ({ phone, name, size = 'sm' }) => {
  const [state, setState] = useState('idle'); // idle | sending | sent | failed
  const timeoutRef = useRef(null);
  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);
  const send = async () => {
    setState('sending');
    try {
      const fd = new FormData();
      fd.append('phone', phone);
      fd.append('message', `Hello ${name || ''}`);
      await whatsappAPI.send(fd);
      setState('sent');
    } catch (err) {
      console.error('[WhatsApp] Send failed:', err);
      setState('failed');
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setState('idle'), 2000);
  };
  const pad = size === 'sm' ? '5px 12px' : '6px 14px';
  const label = state === 'sending' ? '⏳...' : state === 'sent' ? '✅ Sent' : state === 'failed' ? '❌ Failed' : '💬 WhatsApp';
  return (
    <button onClick={send} disabled={state !== 'idle'}
      style={{ padding: pad, background: '#25d366', color: '#fff', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: 12, cursor: state === 'idle' ? 'pointer' : 'default', opacity: state === 'idle' ? 1 : 0.7 }}>
      {label}
    </button>
  );
};

/* ── Supplier Modal ─────────────────────────── */
const SupplierModal = ({ supplier, onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };

  const [form, setForm] = useState({
    name: supplier?.name || '',
    contact_person: supplier?.contact_person || '',
    phone: supplier?.phone || '',
    contact_no: supplier?.contact_no || '',
    email: supplier?.email || '',
    address: supplier?.address || '',
    city: supplier?.city || '',
    state: supplier?.state || 'Maharashtra',
    country: supplier?.country || 'India',
    gstin: supplier?.gstin || '',
    pan: supplier?.pan || '',
    tax_state: supplier?.tax_state || 'Maharashtra',
    opening_balance: supplier?.opening_balance ?? 0,
    op_bal_type: supplier?.op_bal_type || 'debit',
    bank_name: supplier?.bank_name || '',
    bank_ifsc: supplier?.bank_ifsc || '',
    bank_account: supplier?.bank_account || '',
    status: supplier?.status || 'active',
    notes: supplier?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const set = k => e => { setDirty(true); setForm(f => ({ ...f, [k]: e.target.value })); };

  const handleClose = () => {
    if (dirty && !window.confirm('Discard this supplier? All unsaved changes will be lost.')) return;
    onClose();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Supplier name is required'); return; }
    if (!form.phone.trim()) { alert('Phone number is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, opening_balance: parseFloat(form.opening_balance) || 0 };
      if (supplier) await supplierAPI.update(supplier.id, payload);
      else await supplierAPI.create(payload);
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto' }}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, boxSizing: 'border-box', marginTop: 4 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: cardBg, zIndex: 10 }}>
            <h3 style={{ ...pg, fontSize: 17, fontWeight: 800, margin: 0 }}>{supplier ? '✏️ Edit Supplier' : '➕ Add Supplier'}</h3>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>×</button>
          </div>
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Supplier / Company Name<span style={req}>*</span></label><input style={{ ...inp, border: !form.name.trim() ? '1px solid #ef4444' : inp.border }} required value={form.name} onChange={set('name')} placeholder="e.g. Shree Traders" /></div>
            <div><label style={lbl}>Contact Person</label><input style={inp} value={form.contact_person} onChange={set('contact_person')} placeholder="Owner / Manager name" /></div>
            <div><label style={lbl}>Phone<span style={req}>*</span></label><input style={{ ...inp, border: !form.phone.trim() ? '1px solid #ef4444' : inp.border }} required value={form.phone} onChange={set('phone')} placeholder="+91 99999 99999" /></div>
            <div><label style={lbl}>Contact No.</label><input style={inp} value={form.contact_no} onChange={set('contact_no')} placeholder="Alternate contact" /></div>
            <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.email} onChange={set('email')} placeholder="supplier@example.com" /></div>
            <div><label style={lbl}>GSTIN</label><input style={inp} value={form.gstin} onChange={set('gstin')} placeholder="27AABCU9603R1ZX" maxLength={15} /></div>
            <div><label style={lbl}>PAN</label><input style={inp} value={form.pan} onChange={set('pan')} placeholder="AABCU9603R" maxLength={10} /></div>
            <div><label style={lbl}>Tax State</label><input style={inp} value={form.tax_state} onChange={set('tax_state')} placeholder="e.g. Maharashtra" /></div>
            <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Address</label><textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={form.address} onChange={set('address')} placeholder="Full address" /></div>
            <div><label style={lbl}>City</label><input style={inp} value={form.city} onChange={set('city')} placeholder="e.g. Mumbai, Nagpur" /></div>
            <div><label style={lbl}>State</label><input style={inp} value={form.state} onChange={set('state')} placeholder="e.g. Maharashtra" /></div>
            <div><label style={lbl}>Country</label><input style={inp} value={form.country} onChange={set('country')} placeholder="India" /></div>
            <div><label style={lbl}>Opening Balance (₹)</label><input style={inp} type="number" step="0.01" value={form.opening_balance} onChange={set('opening_balance')} placeholder="0.00" /></div>
            <div><label style={lbl}>Balance Type</label><select style={inp} value={form.op_bal_type} onChange={set('op_bal_type')}><option value="debit">Debit</option><option value="credit">Credit</option></select></div>
            <div><label style={lbl}>Status</label>
              <select style={inp} value={form.status} onChange={set('status')}>
                <option value="active">🟢 Active</option><option value="inactive">🔴 Inactive</option>
              </select>
            </div>
            <div><label style={lbl}>Bank Name</label><input style={inp} value={form.bank_name} onChange={set('bank_name')} placeholder="Bank name" /></div>
            <div><label style={lbl}>Bank IFSC</label><input style={inp} value={form.bank_ifsc} onChange={set('bank_ifsc')} placeholder="IFSC code" /></div>
            <div><label style={lbl}>Bank Account No.</label><input style={inp} value={form.bank_account} onChange={set('bank_account')} placeholder="Account number" /></div>
            <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Any notes about this supplier" /></div>
          </div>
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={handleClose} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : supplier ? 'Update Supplier' : 'Add Supplier'}</button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

/* ── Purchase Bill Modal ────────────────────── */
const GST_RATES = [0, 5, 12, 18, 28];
const GST_OPTIONS = GST_RATES.flatMap(r => r === 0 ? [{ value: '0-ex', label: '0%' }] : [{ value: `${r}-in`, label: `${r}%-In` }, { value: `${r}-ex`, label: `${r}%-Ex` }]);
const gstSelectVal = (item) => `${parseFloat(item.gst_percent)||0}-${item.gst_inclusive ? 'in' : 'ex'}`;

const PurchaseBillModal = ({ supplier: supplierProp, suppliers = [], bill, onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const { refreshProducts } = useData();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };

  const emptyItem = () => ({ key: Date.now() + Math.random(), product_id: null, product_name: '', quantity: 1, price: 0, gst_percent: 0, gst_inclusive: false, total: 0, unit: '', hsn_sac: '', discount: 0, description: '' });

  const [chosenSupplier, setChosenSupplier] = useState(supplierProp || null);
  const modalFormRef = React.useRef(null);
  const [form, setForm] = useState({
    bill_type: bill?.bill_type || 'gst',
    bill_number: bill?.bill_number || '',
    bill_date: bill?.bill_date ? bill.bill_date.split('T')[0] : new Date().toISOString().split('T')[0],
    payment_mode: bill?.payment_mode || 'Cash',
    payment_status: bill?.payment_status || 'unpaid',
    transport_name: bill?.transport_name || '',
    transport_amount: bill?.transport_amount ?? '',
    notes: bill?.notes || '',
    discount_amount: bill?.discount_amount ?? '',
    shipping_amount: bill?.shipping_amount ?? '',
    round_off: bill?.round_off ?? '',
    place_of_supply: bill?.place_of_supply || 'Maharashtra',
    po_number: bill?.po_number || '',
    po_date: bill?.po_date ? bill.po_date.split('T')[0] : '',
    tax_method: bill?.tax_method || 'exclusive',
    shipping_tax_rate: bill?.shipping_tax_rate ?? '',
    shipping_cess_rate: bill?.shipping_cess_rate ?? '',
    credit_period: bill?.credit_period ?? '',
    due_date: bill?.due_date ? bill.due_date.split('T')[0] : '',
    eway_bill: bill?.eway_bill || '',
    status: bill?.status || 'received',
    payment_ref: bill?.payment_ref || '',
    tcs_rate: bill?.tcs_rate ?? '',
    tcs_amount: bill?.tcs_amount ?? '',
  });
  const [items, setItems] = useState(bill?.items?.length ? bill.items.map(i => ({ ...i, gst_inclusive: !!i.gst_inclusive, key: Math.random() })) : [emptyItem()]);
  const [partialPaidAmount, setPartialPaidAmount] = useState(() => {
    if (!bill) return '';
    if (bill.payment_status === 'paid') return String(parseFloat(bill.grand_total || 0));
    if (bill.payment_status === 'partial') {
      const paid = parseFloat(bill.paid_amount ?? ((parseFloat(bill.grand_total || 0) || 0) - (parseFloat(bill.balance_amount || 0) || 0)));
      return paid > 0 ? String(paid) : '';
    }
    return '';
  });
  const [saving, setSaving] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProdForm, setNewProdForm] = useState({ name: '', purchase_price: '', selling_price: '', gst_percent: 0 });
  const [newProdSaving, setNewProdSaving] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState([]);
  const [showItemDD, setShowItemDD] = useState(false);
  const itemSearchRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));

  // Self-load supplier list if none passed (handles edge cases from any entry point)
  const [resolvedSuppliers, setResolvedSuppliers] = useState(suppliers);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  useEffect(() => {
    let active = true;
    if (suppliers.length > 0) setResolvedSuppliers(suppliers);
    if (!supplierProp) {
      setSuppliersLoading(true);
      supplierAPI.list({ page_size: 500 })
        .then(r => {
          if (!active) return;
          const fresh = r.data?.suppliers || [];
          setResolvedSuppliers(fresh.length > 0 ? fresh : suppliers);
        })
        .catch(() => {})
        .finally(() => { if (active) setSuppliersLoading(false); });
    }
    return () => { active = false; };
  }, [suppliers, supplierProp]);

  const [dirty, setDirty] = useState(false);
  const isGST = form.bill_type === 'gst';
  const set = k => e => { setDirty(true); setForm(f => ({ ...f, [k]: e.target.value })); };
  const setVal = (k, v) => { setDirty(true); setForm(f => ({ ...f, [k]: v })); };
  const transport = parseFloat(form.transport_amount) || 0;

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Product search for items section
  useEffect(() => {
    if (itemSearch.length >= 1) {
      productAPI.list({ search: itemSearch, limit: 15 })
        .then(r => { setItemSearchResults(r.data.products || []); setShowItemDD(true); })
        .catch(() => {});
    } else {
      setShowItemDD(false);
      setItemSearchResults([]);
    }
  }, [itemSearch]);

  useEffect(() => {
    const handler = e => { if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) setShowItemDD(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('Discard purchase bill? All unsaved changes will be lost.')) return;
    onClose();
  }, [dirty, onClose]);

  // Keyboard shortcuts for Purchase Bill modal in Suppliers page
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { handleClose(); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) { e.preventDefault(); modalFormRef.current?.requestSubmit(); return; }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); modalFormRef.current?.requestSubmit(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  const calcItemBase = (item) => parseFloat(item.quantity || 0) * parseFloat(item.price || 0);
  const calcItemGST = (item) => {
    if (!isGST) return 0;
    const base = calcItemBase(item);
    const gstPct = parseFloat(item.gst_percent || 0);
    if (item.gst_inclusive && gstPct > 0) return base - base / (1 + gstPct / 100);
    return base * (gstPct / 100);
  };
  const calcItemTotal = (item) => {
    if (item.gst_inclusive && isGST) return +calcItemBase(item).toFixed(2);
    return +(calcItemBase(item) + calcItemGST(item)).toFixed(2);
  };
  const calcItemTaxable = (item) => {
    if (!isGST) return calcItemBase(item);
    const base = calcItemBase(item);
    const gstPct = parseFloat(item.gst_percent || 0);
    if (item.gst_inclusive && gstPct > 0) return +(base / (1 + gstPct / 100)).toFixed(2);
    return +base.toFixed(2);
  };

  const totalBase = items.reduce((s, i) => s + calcItemBase(i), 0);
  const calcNetRate = (item) => {
    const total = calcItemTotal(item);
    const base = calcItemBase(item);
    const transportShare = totalBase > 0 ? transport * base / totalBase : 0;
    const qty = parseFloat(item.quantity) || 1;
    return +((total + transportShare) / qty).toFixed(4);
  };

  const handleGSTSelect = (key, compositeVal) => {
    const [rate, mode] = compositeVal.split('-');
    setItems(prev => prev.map(i => i.key !== key ? i : { ...i, gst_percent: parseFloat(rate), gst_inclusive: mode === 'in' }));
  };

  const updateItem = (key, field, val) => {
    setDirty(true);
    setItems(prev => prev.map(i => {
      if (i.key !== key) return i;
      return { ...i, [field]: val };
    }));
  };

  const removeItem = key => { setDirty(true); setItems(prev => prev.filter(i => i.key !== key)); };

  const subtotal = items.reduce((s, i) => s + calcItemTaxable(i), 0);
  const taxTotal = isGST ? items.reduce((s, i) => s + calcItemGST(i), 0) : 0;
  const grandTotal = items.reduce((s, i) => s + calcItemTotal(i), 0) + transport;
  const normalizedPartialPaid = Math.min(Math.max(parseFloat(partialPaidAmount || 0) || 0, 0), grandTotal);
  const settledPaidAmount = form.payment_status === 'paid'
    ? grandTotal
    : form.payment_status === 'partial'
      ? normalizedPartialPaid
      : 0;
  const settledBalanceAmount = Math.max(0, +(grandTotal - settledPaidAmount).toFixed(2));

  const addProductToItems = useCallback((p) => {
    setDirty(true);
    setItems(prev => {
      const existing = prev.find(i => i.product_id === p.id);
      if (existing) {
        return prev.map(i => i.product_id === p.id ? { ...i, quantity: (parseFloat(i.quantity) || 1) + 1 } : i);
      }
      return [...prev, {
        key: Date.now() + Math.random(),
        product_id: p.id,
        product_name: p.name,
        quantity: 1,
        price: parseFloat(p.purchase_price) || parseFloat(p.selling_price) || 0,
        gst_percent: parseFloat(p.gst_percent) || 0,
        gst_inclusive: false,
        total: 0,
        unit: p.unit || '',
        hsn_sac: p.hsn_code || p.hsn_sac || '',
        discount: 0,
        description: ''
      }];
    });
    setItemSearch('');
    setShowItemDD(false);
  }, []);

  const quickAddProduct = async () => {
    if (!newProdForm.name.trim()) return;
    setNewProdSaving(true);
    try {
      const res = await productAPI.create({
        name: newProdForm.name.trim(),
        sku: 'AUTO-' + Date.now(),
        purchase_price: parseFloat(newProdForm.purchase_price) || 0,
        selling_price: parseFloat(newProdForm.selling_price) || parseFloat(newProdForm.purchase_price) || 0,
        gst_percent: parseFloat(newProdForm.gst_percent) || 0
      });
      const p = res.data;
      setNewProdForm({ name: '', purchase_price: '', selling_price: '', gst_percent: 0 });
      setShowAddProductModal(false);
      refreshProducts(true);
      addProductToItems(p);
    } catch (e) { alert('Failed to create product: ' + (e.response?.data?.detail || e.message)); }
    setNewProdSaving(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const supplier = chosenSupplier;
    if (!supplier) { alert('Please select a supplier'); return; }
    if (items.some(i => !i.product_name.trim())) { alert('All items must have a name'); return; }
    if (items.some(i => !parseFloat(i.price) || parseFloat(i.price) <= 0)) { alert('All items must have a valid price'); return; }
    if (form.payment_status === 'partial' && (normalizedPartialPaid <= 0 || normalizedPartialPaid >= grandTotal)) {
      alert('For a partial bill, enter a paid amount greater than zero and less than the grand total.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        supplier_name: supplier.name,
        bill_type: form.bill_type,
        bill_number: form.bill_number,
        bill_date: form.bill_date,
        payment_mode: form.payment_mode,
        payment_status: form.payment_status,
        transport_name: form.transport_name || null,
        transport_amount: transport,
        notes: form.notes,
        discount_amount: parseFloat(form.discount_amount) || 0,
        shipping_amount: parseFloat(form.shipping_amount) || 0,
        round_off: parseFloat(form.round_off) || 0,
        place_of_supply: form.place_of_supply || null,
        po_number: form.po_number || null,
        po_date: form.po_date || null,
        tax_method: form.tax_method || 'exclusive',
        shipping_tax_rate: parseFloat(form.shipping_tax_rate) || 0,
        shipping_cess_rate: parseFloat(form.shipping_cess_rate) || 0,
        credit_period: form.credit_period ? String(form.credit_period).trim() : null,
        due_date: form.due_date || null,
        eway_bill: form.eway_bill || null,
        status: form.status || 'received',
        payment_ref: form.payment_ref || null,
        tcs_rate: parseFloat(form.tcs_rate) || 0,
        tcs_amount: parseFloat(form.tcs_amount) || 0,
        cgst_amount: +(taxTotal / 2).toFixed(2),
        sgst_amount: +(taxTotal / 2).toFixed(2),
        igst_amount: 0,
        cess_amount: 0,
        items: items.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: Math.max(1, parseInt(i.quantity, 10) || 1),
          price: parseFloat(i.price) || 0,
          gst_percent: isGST ? (parseFloat(i.gst_percent) || 0) : 0,
          gst_inclusive: !!i.gst_inclusive,
          total: calcItemTotal(i),
          net_rate: calcNetRate(i),
          unit: i.unit || null,
          hsn_sac: i.hsn_sac || null,
          discount: parseFloat(i.discount) || 0,
          description: i.description || null,
          cgst_rate: isGST ? (parseFloat(i.gst_percent) || 0) / 2 : 0,
          sgst_rate: isGST ? (parseFloat(i.gst_percent) || 0) / 2 : 0,
          igst_rate: 0,
          cess_rate: 0,
          is_igst: false,
          price_before_discount: parseFloat(i.price) || 0,
        })),
        subtotal: +subtotal.toFixed(2),
        tax_total: +taxTotal.toFixed(2),
        grand_total: +grandTotal.toFixed(2),
        paid_amount: +settledPaidAmount.toFixed(2),
        balance_amount: +settledBalanceAmount.toFixed(2),
      };
      if (bill) await supplierAPI.updateBill(bill.id, payload);
      else await supplierAPI.createBill(supplier.id, payload);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d?.msg || d?.message).filter(Boolean).join(', ')
          : (err.response?.data?.message || err.message || 'Save failed');
      alert(msg);
      console.error(err);
    }
    setSaving(false);
  };

  const filteredSuppliers = resolvedSuppliers.filter(s => (s.name || '').toLowerCase().includes((supplierSearch || '').toLowerCase()));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: isMobileView ? 4 : 10, overflowY: 'auto' }}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: isMobileView ? 12 : 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: isMobileView ? 8 : 12, boxSizing: 'border-box', marginTop: isMobileView ? 2 : 4 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
        {/* Decorative accent bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        <form ref={modalFormRef} onSubmit={handleSubmit}>
          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, position: 'sticky', top: 0, background: cardBg, zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
              <h3 style={{ ...pg, fontSize: 15, fontWeight: 800, margin: 0 }}>
                {bill ? '✏️ Edit Purchase Bill' : '📦 New Purchase Bill'}
                {chosenSupplier ? ` — ${chosenSupplier.name}` : ''}
              </h3>
              {/* GST / NON-GST toggle */}
              <div style={{ display: 'flex', background: darkMode ? '#0F172A' : '#F1F5F9', borderRadius: 8, padding: 2, gap: 2 }}>
                {[['gst', '📋 GST'], ['non_gst', '🧾 NON-GST']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setVal('bill_type', val)}
                    style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                      background: form.bill_type === val ? acc : 'transparent',
                      color: form.bill_type === val ? '#fff' : (darkMode ? '#64748B' : '#94A3B8') }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>×</button>
          </div>

          <div style={{ padding: isMobileView ? '12px 10px' : '16px 20px' }}>
            {/* Supplier selector (when no supplier pre-selected) */}
            {!supplierProp && (
              <div style={{ marginBottom: 14, padding: 12, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, border: `1px solid ${border}` }}>
                <label style={lbl}>Select Supplier<span style={req}>*</span></label>
                {chosenSupplier ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: acc }}>{chosenSupplier.name}</span>
                    <button type="button" onClick={() => { setChosenSupplier(null); setDirty(true); }} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Change</button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input style={inp} placeholder="Type supplier name to search…" value={supplierSearch} onChange={e => setSupplierSearch(e.target.value)} autoFocus />
                    {supplierSearch.trim().length > 0 && (
                      suppliersLoading ? (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, zIndex: 20, maxHeight: 160, overflow: 'auto', marginTop: 2, padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>
                          Loading suppliers...
                        </div>
                      ) : filteredSuppliers.length > 0 ? (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, zIndex: 20, maxHeight: 200, overflow: 'auto', marginTop: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
                          {filteredSuppliers.map(s => (
                            <div key={s.id} onClick={() => { setChosenSupplier(s); setSupplierSearch(''); setDirty(true); }}
                              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${border}` }} tabIndex={0} data-supplier-item
                              onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1E293B' : '#F1F5F9'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { setChosenSupplier(s); setSupplierSearch(''); setDirty(true); }
                                if (e.key === 'ArrowUp' && e.target.previousSibling) e.target.previousSibling.focus();
                                if (e.key === 'ArrowDown' && e.target.nextSibling) e.target.nextSibling.focus();
                              }}>
                              {s.name} {s.phone && <span style={{ color: darkMode ? '#64748B' : '#94A3B8', fontSize: 11 }}>· {s.phone}</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, zIndex: 20, maxHeight: 160, overflow: 'auto', marginTop: 2, padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>
                          No suppliers found. Please add a supplier first.
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Bill header fields */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
              <div><label style={lbl}>Bill / Invoice No.<span style={req}>*</span></label><input style={{ ...inp, border: !form.bill_number.trim() ? '1px solid #ef4444' : inp.border }} required value={form.bill_number} onChange={set('bill_number')} placeholder="Supplier's bill #" /></div>
              <div><label style={lbl}>Bill Date<span style={req}>*</span></label><StrictDateInput style={inp}  required value={form.bill_date} onChange={set('bill_date')} /></div>
              <div><label style={lbl}>Payment Mode</label>
                <select style={inp} value={form.payment_mode} onChange={set('payment_mode')}>
                  {['Cash','Card','UPI','Bank Transfer','Credit','Cheque'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Payment Status</label>
                <select style={inp} value={form.payment_status} onChange={set('payment_status')}>
                  <option value="paid">✅ Paid</option>
                  <option value="unpaid">⏳ Unpaid</option>
                  <option value="partial">🔶 Partial</option>
                </select>
              </div>
              {form.payment_status === 'partial' && (
                <div><label style={lbl}>Paid Amount (₹)</label>
                  <input
                    style={inp}
                    type="number"
                    min="0"
                    step="0.01"
                    value={partialPaidAmount}
                    onChange={e => { setDirty(true); setPartialPaidAmount(e.target.value); }}
                    placeholder="0.00"
                  />
                </div>
              )}
              <div><label style={lbl}>Place of Supply</label><input style={inp} value={form.place_of_supply} onChange={set('place_of_supply')} placeholder="State / UT" /></div>
              <div><label style={lbl}>Tax Method</label>
                <select style={inp} value={form.tax_method} onChange={set('tax_method')}>
                  <option value="exclusive">Exclusive</option>
                  <option value="inclusive">Inclusive</option>
                </select>
              </div>
              <div><label style={lbl}>Bill Status</label>
                <select style={inp} value={form.status} onChange={set('status')}>
                  {['received','pending','returned','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Payment Ref</label><input style={inp} value={form.payment_ref} onChange={set('payment_ref')} placeholder="Txn / Ref #" /></div>
            </div>

            {/* PO & Credit Details */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14, padding: '10px 14px', background: darkMode ? '#0F172A' : '#F0F9FF', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#BAE6FD'}` }}>
              <div><label style={{ ...lbl, color: '#0284c7' }}>PO Number</label><input style={inp} value={form.po_number} onChange={set('po_number')} placeholder="Purchase order #" /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>PO Date</label><StrictDateInput style={inp}  value={form.po_date} onChange={set('po_date')} /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>Credit Period (Days)</label><input style={inp} type="number" min="0" value={form.credit_period} onChange={set('credit_period')} placeholder="0" /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>Due Date</label><StrictDateInput style={inp}  value={form.due_date} onChange={set('due_date')} /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>E-Way Bill</label><input style={inp} value={form.eway_bill} onChange={set('eway_bill')} placeholder="E-Way bill number" /></div>
            </div>

            {/* Transport & Charges section */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14, padding: '10px 14px', background: darkMode ? '#0F172A' : '#FFFBEB', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#FDE68A'}` }}>
              <div><label style={{ ...lbl, color: '#d97706' }}>🚚 Transport / Freight</label>
                <input style={inp} value={form.transport_name} onChange={set('transport_name')} placeholder="Carrier / lorry name" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Transport Amt (₹)</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.transport_amount} onChange={set('transport_amount')} placeholder="0.00" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Shipping Amt (₹)</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.shipping_amount} onChange={set('shipping_amount')} placeholder="0.00" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Shipping Tax %</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.shipping_tax_rate} onChange={set('shipping_tax_rate')} placeholder="0" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Shipping Cess %</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.shipping_cess_rate} onChange={set('shipping_cess_rate')} placeholder="0" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Discount (₹)</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.discount_amount} onChange={set('discount_amount')} placeholder="0.00" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Round Off (₹)</label>
                <input style={inp} type="number" step="0.01" value={form.round_off} onChange={set('round_off')} placeholder="0.00" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>TCS Rate %</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.tcs_rate} onChange={set('tcs_rate')} placeholder="0" />
              </div>
              <div><label style={{ ...lbl, color: '#d97706' }}>TCS Amount (₹)</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.tcs_amount} onChange={set('tcs_amount')} placeholder="0.00" />
              </div>
              {transport > 0 && <div style={{ gridColumn: '1/-1', fontSize: 11, color: darkMode ? '#64748B' : '#92400e' }}>
                ℹ️ Transport cost will be distributed proportionally across items and included in net rate per unit
              </div>}
            </div>

            {/* Items table */}
            <div style={{ fontWeight: 700, fontSize: 13, ...pg, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              � Items<span style={req}>*</span>
              {!isGST && <span style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8', fontWeight: 400 }}>(NON-GST mode — prices are inclusive)</span>}
            </div>

            {/* Product search bar — same pattern as sale invoice */}
            <div ref={itemSearchRef} style={{ position: 'relative', marginBottom: 12 }}>
              <input
                style={{ ...inp, paddingLeft: 36 }}
                value={itemSearch}
                onChange={e => setItemSearch(e.target.value)}
                placeholder="Search products by name or SKU to add…"
              />
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: darkMode ? '#64748B' : '#94A3B8' }}>🔍</span>
              {showItemDD && itemSearchResults.length > 0 && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 30, background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: isMobileView ? 220 : 260, overflowY: 'auto' }}>
                  {itemSearchResults.map(p => (
                    <button key={p.id} type="button" onClick={() => addProductToItems(p)}
                      style={{ width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: darkMode ? '#F1F5F9' : '#0F172A' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: darkMode ? '#64748B' : '#94A3B8' }}>
                          SKU: {p.sku}{p.hsn_sac ? ` · HSN: ${p.hsn_sac}` : ''}{p.unit ? ` · ${p.unit}` : ''} · GST: {p.gst_percent}%
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                        <div style={{ fontWeight: 700, color: acc, fontSize: 13 }}>₹{parseFloat(p.purchase_price || p.selling_price || 0).toFixed(2)}</div>
                        <div style={{ fontSize: 10, color: parseFloat(p.current_stock) <= 0 ? '#dc2626' : '#16a34a' }}>
                          {parseFloat(p.current_stock) <= 0 ? '❌ 0' : `📦 ${p.current_stock}`} {p.unit}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Items table */}
            {items.filter(i => i.product_id || i.product_name).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', color: darkMode ? '#64748B' : '#94A3B8', fontSize: 13, border: `1.5px dashed ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 8, marginBottom: 12 }}>
                🔍 Search for a product above to begin adding items
              </div>
            ) : (
            <div style={{ border: `1px solid ${border}`, borderRadius: 10, marginBottom: 12, overflow: 'visible' }}>
              <div style={{ overflowX: 'auto', overflowY: 'visible', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobileView ? (isGST ? 760 : 660) : (isGST ? 900 : 750) }}>
                <thead>
                  <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                    {['Product / Item', 'HSN/SAC', 'Unit', 'Qty', 'Price (₹)', 'Disc (₹)', ...(isGST ? ['GST %'] : []), 'Item Total', 'Net Rate/Unit', ''].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const netRate = calcNetRate(item);
                    return (
                      <tr key={item.key} style={{ borderTop: `1px solid ${border}` }}>
                        <td style={{ padding: '6px 8px', minWidth: 200 }}>
                          <input style={{ ...inp, padding: '5px 8px' }}
                            value={item.product_name}
                            onChange={e => updateItem(item.key, 'product_name', e.target.value)}
                            placeholder="Item name" />
                        </td>
                        <td style={{ padding: '6px 6px', width: 80 }}>
                          <input style={{ ...inp, padding: '5px 6px' }} value={item.hsn_sac || ''} onChange={e => updateItem(item.key, 'hsn_sac', e.target.value)} placeholder="HSN" />
                        </td>
                        <td style={{ padding: '6px 6px', width: 60 }}>
                          <input style={{ ...inp, padding: '5px 6px' }} value={item.unit || ''} onChange={e => updateItem(item.key, 'unit', e.target.value)} placeholder="Pcs" />
                        </td>
                        <td style={{ padding: '6px 6px', width: 60 }}>
                          <input style={{ ...inp, padding: '5px 6px' }} type="number" min="1" value={item.quantity} onChange={e => updateItem(item.key, 'quantity', e.target.value)} />
                        </td>
                        <td style={{ padding: '6px 6px', width: 100 }}>
                          <input style={{ ...inp, padding: '5px 6px' }} type="number" min="0" step="0.01" value={item.price} onChange={e => updateItem(item.key, 'price', e.target.value)} />
                        </td>
                        <td style={{ padding: '6px 6px', width: 70 }}>
                          <input style={{ ...inp, padding: '5px 6px' }} type="number" min="0" step="0.01" value={item.discount || ''} onChange={e => updateItem(item.key, 'discount', e.target.value)} placeholder="0" />
                        </td>
                        {isGST && (
                          <td style={{ padding: '6px 6px', width: 70 }}>
                            <select style={{ ...inp, padding: '5px 6px' }} value={gstSelectVal(item)} onChange={e => handleGSTSelect(item.key, e.target.value)}>
                              {GST_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                        )}
                        <td style={{ padding: '6px 10px', fontWeight: 700, color: acc, whiteSpace: 'nowrap', fontSize: 13 }}>
                          ₹{fmtNum(calcItemTotal(item))}
                        </td>
                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', background: darkMode ? '#14532d22' : '#dcfce7', padding: '2px 8px', borderRadius: 6 }}>
                            ₹{fmtNum(netRate)}
                          </span>
                          {transport > 0 && <div style={{ fontSize: 10, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 1 }}>incl. transport</div>}
                        </td>
                        <td style={{ padding: '6px 6px' }}>
                          <button type="button" onClick={() => removeItem(item.key)} disabled={items.length === 1}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: '2px 6px' }}>×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => { setDirty(true); setItems(p => [...p, { key: Date.now() + Math.random(), product_id: null, product_name: '', quantity: 1, price: 0, gst_percent: 0, gst_inclusive: false, total: 0, unit: '', hsn_sac: '', discount: 0, description: '' }]); }}
                style={{ padding: '6px 14px', background: 'transparent', border: `1px dashed ${acc}`, color: acc, borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                + Add Manual Item
              </button>
              <button type="button" onClick={() => setShowAddProductModal(true)}
                style={{ padding: '6px 14px', background: acc + '15', border: `1px solid ${acc}`, color: acc, borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                ➕ New Product
              </button>
            </div>

            {/* Totals + Notes */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={lbl}>Notes</label>
                <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Any notes…" />
              </div>
              <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, padding: '14px 18px', border: `1px solid ${border}`, minWidth: 0, width: '100%', maxWidth: 320 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}>
                  <span>Subtotal:</span><span>₹{fmtNum(subtotal)}</span>
                </div>
                {isGST && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}>
                    <span>GST:</span><span>₹{fmtNum(taxTotal)}</span>
                  </div>
                )}
                {(parseFloat(form.discount_amount) || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: '#dc2626', marginBottom: 4 }}>
                    <span>Discount:</span><span>-₹{fmtNum(parseFloat(form.discount_amount) || 0)}</span>
                  </div>
                )}
                {transport > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: '#d97706', marginBottom: 4 }}>
                    <span>🚚 Transport:</span><span>₹{fmtNum(transport)}</span>
                  </div>
                )}
                {(parseFloat(form.shipping_amount) || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: '#d97706', marginBottom: 4 }}>
                    <span>Shipping:</span><span>₹{fmtNum(parseFloat(form.shipping_amount) || 0)}</span>
                  </div>
                )}
                {(parseFloat(form.round_off) || 0) !== 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}>
                    <span>Round Off:</span><span>₹{fmtNum(parseFloat(form.round_off) || 0)}</span>
                  </div>
                )}
                {(parseFloat(form.tcs_amount) || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}>
                    <span>TCS ({form.tcs_rate || 0}%):</span><span>₹{fmtNum(parseFloat(form.tcs_amount) || 0)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 16, fontWeight: 800, color: acc, borderTop: `1px solid ${border}`, paddingTop: 8, marginTop: 4 }}>
                  <span>Grand Total:</span><span>₹{fmtNum(grandTotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: '#16a34a', marginTop: 8 }}>
                  <span>Paid:</span><span>₹{fmtNum(settledPaidAmount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 13, color: settledBalanceAmount > 0 ? '#dc2626' : (darkMode ? '#94A3B8' : '#64748B') }}>
                  <span>Balance:</span><span>₹{fmtNum(settledBalanceAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${border}`, display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', position: 'sticky', bottom: 0, background: cardBg, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: dirty ? '#f59e0b' : (darkMode ? '#64748B' : '#94A3B8') }}>
              {dirty ? 'Unsaved changes will ask for confirmation before closing.' : 'All amounts update supplier due balances automatically.'}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={handleClose}
              style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: '9px 28px', background: saving ? '#94A3B8' : acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
              {saving ? '⏳ Saving…' : bill ? '💾 Update Bill' : '💾 Save Bill'}
            </button>
            </div>
          </div>
        </form>

        {/* Add Product Modal */}
        {showAddProductModal && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto', borderRadius: 'inherit' }}>
            <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, boxSizing: 'border-box', marginTop: 40 }}>
              <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
                <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ ...pg, fontSize: 16, fontWeight: 800, margin: 0 }}>➕ Create New Product</h3>
                    <button type="button" onClick={() => { setShowAddProductModal(false); setNewProdForm({ name: '', purchase_price: '', selling_price: '', gst_percent: 0 }); }}
                      style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>×</button>
                  </div>

                  <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={lbl}>Product Name <span style={req}>*</span></label>
                      <input style={{ ...inp, border: !newProdForm.name.trim() ? '1px solid #ef4444' : inp.border }}
                        value={newProdForm.name}
                        onChange={e => setNewProdForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g., Widget Pro 100" autoFocus />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={lbl}>Purchase Price (₹)</label>
                        <input style={inp}
                          type="number" min="0" step="0.01"
                          value={newProdForm.purchase_price}
                          onChange={e => setNewProdForm(f => ({ ...f, purchase_price: e.target.value }))}
                          placeholder="0.00" />
                      </div>
                      <div>
                        <label style={lbl}>Selling Price (₹)</label>
                        <input style={inp}
                          type="number" min="0" step="0.01"
                          value={newProdForm.selling_price}
                          onChange={e => setNewProdForm(f => ({ ...f, selling_price: e.target.value }))}
                          placeholder="Auto-calculate" />
                      </div>
                    </div>

                    {isGST && (
                      <div>
                        <label style={lbl}>GST %</label>
                        <select style={inp} value={newProdForm.gst_percent} onChange={e => setNewProdForm(f => ({ ...f, gst_percent: e.target.value }))}>
                          {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => { setShowAddProductModal(false); setNewProdForm({ name: '', purchase_price: '', selling_price: '', gst_percent: 0 }); }}
                      style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>
                      Cancel
                    </button>
                    <button type="button" disabled={newProdSaving || !newProdForm.name.trim()} onClick={quickAddProduct}
                      style={{ padding: '9px 28px', background: newProdSaving || !newProdForm.name.trim() ? '#94A3B8' : acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: newProdSaving || !newProdForm.name.trim() ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                      {newProdSaving ? '⏳ Saving…' : '✓ Create Product'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
      </div>
    </div>
  );
};

/* ── Main Suppliers Page ─────────────────────── */
const Suppliers = () => {
  const { darkMode, color } = useTheme();
  const { suppliers: sharedSuppliers, refreshSuppliers, refreshProducts } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };

  const [tab, setTab] = useState('suppliers');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [cameFromDash, setCameFromDash] = useState(false);
  const navigate = useNavigate();

  // Auto-open purchase bill modal from URL param (Dashboard quick action)
  const pendingAction = useRef(searchParams.get('action'));
  useEffect(() => {
    if (pendingAction.current === 'purchase') {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        return next;
      }, { replace: true });
    }
  }, [setSearchParams]);
  const [billsForSupplier, setBillsForSupplier] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [billSearch, setBillSearch] = useState('');
  const [billStatus, setBillStatus] = useState('');
  const [outstanding, setOutstanding] = useState([]);
  const [supplierDetailTab, setSupplierDetailTab] = useState('bills');
  const [supplierLedger, setSupplierLedger] = useState([]);
  const [supplierLedgerLoading, setSupplierLedgerLoading] = useState(false);
  const [supplierLedgerFetched, setSupplierLedgerFetched] = useState(false);
  const searchRef = useRef(null);

  const selectedSupplier = suppliers.find(s => s.id === selectedId);
  const supplierLedgerWithBalance = supplierLedger;

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await supplierAPI.list({ search: search || undefined, page_size: 50 });
      setSuppliers(r.data.suppliers || []);
    } catch {}
    setLoading(false);
  }, [search]);

  const loadAllBills = useCallback(async () => {
    try {
      const r = await supplierAPI.listAllBills({ search: billSearch || undefined, status: billStatus || undefined });
      setAllBills(r.data.bills || []);
    } catch {}
  }, [billSearch, billStatus]);

  const loadOutstanding = useCallback(async () => {
    try {
      const r = await supplierAPI.outstanding();
      setOutstanding(r.data || []);
    } catch {}
  }, []);

  const loadBillsForSupplier = useCallback(async (supplierId) => {
    if (!supplierId) return;
    try {
      const r = await supplierAPI.listBills(supplierId);
      setBillsForSupplier(r.data.bills || []);
    } catch {}
  }, []);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);
  useEffect(() => { if (tab === 'bills') loadAllBills(); }, [tab, loadAllBills]);
  useEffect(() => { if (tab === 'outstanding') loadOutstanding(); }, [tab, loadOutstanding]);
  useEffect(() => { if (selectedId && tab === 'suppliers') loadBillsForSupplier(selectedId); }, [selectedId, tab, loadBillsForSupplier]);
  useEffect(() => { setSupplierDetailTab('bills'); setSupplierLedger([]); setSupplierLedgerFetched(false); }, [selectedId]);
  useEffect(() => {
    if (supplierDetailTab === 'ledger' && selectedId && !supplierLedgerFetched && !supplierLedgerLoading) {
      setSupplierLedgerLoading(true);
      paymentAPI.getLedger('supplier', selectedId)
        .then(r => setSupplierLedger(Array.isArray(r.data?.entries) ? r.data.entries : Array.isArray(r.data) ? r.data : []))
        .catch(() => setSupplierLedger([]))
        .finally(() => { setSupplierLedgerLoading(false); setSupplierLedgerFetched(true); });
    }
  }, [supplierDetailTab, selectedId, supplierLedgerFetched, supplierLedgerLoading]);

  // Auto-open purchase bill modal after suppliers loaded (from dashboard quick action)
  useEffect(() => {
    if (pendingAction.current === 'purchase' && !loading && suppliers.length > 0) {
      pendingAction.current = null;
      setEditBill(null);
      setCameFromDash(true);
      setShowBillModal(true);
    }
  }, [loading, suppliers]);

  // Global keyboard shortcuts for Suppliers page
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+N: Create new supplier
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && e.shiftKey === false) {
        e.preventDefault();
        setEditSupplier(null);
        setShowSupplierModal(true);
      }
      // Ctrl+B: Create new purchase bill
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        setEditBill(null);
        setShowBillModal(true);
      }
      // Ctrl+?: Show keyboard help
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === '?') {
        e.preventDefault();
        alert('Keyboard Shortcuts:\nCtrl+N - Create new supplier\nCtrl+B - Create new purchase bill\nCtrl+E - Edit selected supplier\nDelete - Delete selected supplier\nEsc - Deselect');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []); // eslint-disable-next-line react-hooks/exhaustive-deps
  // Keyboard navigation
  useEffect(() => {
    const handler = e => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const idx = suppliers.findIndex(s => s.id === selectedId);
        if (idx === -1 && suppliers.length) setSelectedId(suppliers[0].id);
        else if (idx < suppliers.length - 1) setSelectedId(suppliers[idx + 1].id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = suppliers.findIndex(s => s.id === selectedId);
        if (idx > 0) setSelectedId(suppliers[idx - 1].id);
      } else if (e.key === 'n' || e.key === 'N') { e.preventDefault(); setEditSupplier(null); setShowSupplierModal(true); }
      else if (e.key === 'e' || e.key === 'E') { if (selectedSupplier) { setEditSupplier(selectedSupplier); setShowSupplierModal(true); } }
      else if (e.key === 'b' || e.key === 'B') { if (selectedSupplier) { setEditBill(null); setShowBillModal(true); } }
      else if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
      else if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, selectedSupplier, suppliers]);

  // Listen for sv-open-modal custom event to open add supplier modal
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'add-supplier') {
        setEditSupplier(null);
        setShowSupplierModal(true);
      }
    };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, []);

  const deleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier? All their purchase bills will also be deleted.')) return;
    try { await supplierAPI.delete(id); setSelectedId(null); loadSuppliers(); refreshSuppliers(true); } catch { alert('Delete failed'); }
  };

  const statusBadge = s => {
    const map = { paid: '#dcfce7:#166534', unpaid: '#fee2e2:#991b1b', partial: '#fef9c3:#854d0e' };
    const [bg, tx] = (map[s] || '#f1f5f9:#374151').split(':');
    return <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color: tx }}>{s}</span>;
  };

  const tabs = [
    { id: 'suppliers', label: '🏭 Suppliers' },
    { id: 'bills', label: '📦 Purchase Bills' },
    { id: 'outstanding', label: '💰 Outstanding' },
  ];

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ ...pg, fontSize: 26, fontWeight: 800, margin: 0 }}>Suppliers</h1>
          <p style={{ ...muted, margin: '3px 0 0', fontSize: 13 }}>Manage suppliers, purchase bills & outstanding payments</p>
        </div>

        {/* Tab bar */}
        <div style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginBottom: 20, display: 'flex', gap: 4 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '9px 22px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
              background: 'transparent', borderBottom: tab === t.id ? `3px solid ${acc}` : '3px solid transparent',
              color: tab === t.id ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2, transition: 'all 0.2s',
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── Suppliers Tab ── */}
        {tab === 'suppliers' && (
          <div style={{ display: 'grid', gridTemplateColumns: selectedId ? '1fr 380px' : '1fr', gap: 16 }}>
            {/* Left: list */}
            <div>
              {/* Search + actions */}
              <div style={{ ...card, padding: 12, marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <input ref={searchRef} style={{ ...inp, flex: 1, minWidth: 180 }} placeholder="/ Search suppliers…" value={search} onChange={e => setSearch(e.target.value)} />
                <button onClick={() => { setEditSupplier(null); setShowSupplierModal(true); }}
                  style={{ padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  ➕ Add Supplier <span style={{ fontSize: 10, opacity: 0.8 }}>(N)</span>
                </button>
                <button onClick={async () => { try { const r = await bulkAPI.exportSuppliers(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'suppliers_export.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Export failed'); } }}
                  style={{ padding: '8px 14px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  📤 Export
                </button>
                <label style={{ padding: '8px 14px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'inline-block' }}>
                  📥 Import
                  <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await bulkAPI.importSuppliers(f); alert(`Import done! ${r.data.imported} imported, ${r.data.skipped} skipped.${r.data.errors?.length ? '\nErrors: ' + r.data.errors.join('\n') : ''}`); window.location.reload(); } catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); } e.target.value = ''; }} />
                </label>
                <button onClick={async () => { try { const r = await bulkAPI.supplierTemplate(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'suppliers_template.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Download failed'); } }}
                  style={{ padding: '8px 10px', background: 'transparent', color: acc, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                  📋 Template
                </button>
              </div>
              <div style={{ fontSize: 11, color: darkMode ? '#475569' : '#94A3B8', marginBottom: 8 }}>
                💡 <strong>↑↓ navigate</strong> · <kbd style={{ padding: '1px 5px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 4, background: darkMode ? '#1E293B' : '#F1F5F9' }}>E</kbd> Edit &nbsp;
                <kbd style={{ padding: '1px 5px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 4, background: darkMode ? '#1E293B' : '#F1F5F9' }}>B</kbd> New Bill &nbsp;
                <kbd style={{ padding: '1px 5px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 4, background: darkMode ? '#1E293B' : '#F1F5F9' }}>/</kbd> Search
              </div>
              <div className="stagger-list" style={{ ...card, overflow: 'auto' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 50, color: darkMode ? '#64748B' : '#94A3B8' }}>Loading…</div>
                ) : suppliers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 60 }}>
                    <div style={{ fontSize: 48, marginBottom: 10 }}>🏭</div>
                    <p style={{ color: darkMode ? '#64748B' : '#94A3B8' }}>No suppliers yet</p>
                    <button onClick={() => setShowSupplierModal(true)} style={{ padding: '9px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Add First Supplier</button>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                        {['Supplier', 'Phone', 'City', 'GSTIN', 'Status', 'Action'].map(h => (
                          <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Action' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.map(s => {
                        const isSel = s.id === selectedId;
                        return (
                          <tr key={s.id} onClick={() => setSelectedId(isSel ? null : s.id)}
                            style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, cursor: 'pointer', background: isSel ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', outline: isSel ? `2px solid ${acc}` : 'none', outlineOffset: -1 }}>
                            <td style={{ padding: '10px 12px' }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: isSel ? acc : (darkMode ? '#F1F5F9' : '#0F172A') }}>{s.name}</div>
                              {s.contact_person && <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8' }}>{s.contact_person}</div>}
                              {parseFloat(s.outstanding) > 0 && (
                                <div style={{ marginTop: 2, display: 'inline-block', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#991b1b' }}>
                                  ₹{(parseFloat(s.outstanding)||0).toLocaleString('en-IN',{minimumFractionDigits:2})} due
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B' }}>{s.phone || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B' }}>{[s.city, s.state].filter(Boolean).join(', ') || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', color: darkMode ? '#94A3B8' : '#64748B' }}>{s.gstin || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 12 }}>{s.status ? <span style={{ padding: '2px 7px', borderRadius: 5, fontWeight: 700, fontSize: 10, background: s.status === 'active' ? '#dcfce7' : '#fef2f2', color: s.status === 'active' ? '#16a34a' : '#dc2626' }}>{s.status}</span> : '—'}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {isSel && (
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                  <button title="Edit (E)" onClick={e => { e.stopPropagation(); setEditSupplier(s); setShowSupplierModal(true); }} style={{ padding: '5px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>✏️ Edit</button>
                                  <button title="New Bill (B)" onClick={e => { e.stopPropagation(); setEditBill(null); setShowBillModal(true); }} style={{ padding: '5px 10px', background: acc, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>📦 Bill</button>
                                  <button title="Delete" onClick={e => { e.stopPropagation(); deleteSupplier(s.id); }} style={{ padding: '5px 8px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Right: supplier detail panel */}
            {selectedSupplier && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ ...card, padding: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ ...pg, fontSize: 17, fontWeight: 800, marginBottom: 2 }}>{selectedSupplier.name}</div>
                      {selectedSupplier.contact_person && <div style={{ ...muted, fontSize: 13, marginBottom: 6 }}>👤 {selectedSupplier.contact_person}</div>}
                    </div>
                    <button onClick={() => setSelectedId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8', fontSize: 18 }}>×</button>
                  </div>
                  {/* Live balance stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[['Outstanding', `₹${(parseFloat(selectedSupplier.outstanding)||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`, '#dc2626'],
                      ['Total Purchased', `₹${(parseFloat(selectedSupplier.total_purchased)||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`, acc],
                      ['Bills', selectedSupplier.bill_count || 0, darkMode ? '#94A3B8' : '#64748B']
                    ].map(([label, val, clr]) => (
                      <div key={label} style={{ textAlign: 'center', padding: '8px 4px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, border: `1px solid ${darkMode ? '#1E293B' : '#E2E8F0'}` }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: clr }}>{val}</div>
                        <div style={{ fontSize: 10, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 1 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {selectedSupplier.phone && <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <a href={`tel:${selectedSupplier.phone}`} style={{ padding: '5px 12px', background: '#16a34a', color: '#fff', borderRadius: 7, textDecoration: 'none', fontWeight: 700, fontSize: 12 }}>📞 Call</a>
                    <WhatsAppButton phone={selectedSupplier.phone} name={selectedSupplier.name} size="sm" />
                    <span style={{ fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B' }}>{selectedSupplier.phone}</span>
                  </div>}
                  {selectedSupplier.email && <div style={{ fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}>📧 {selectedSupplier.email}</div>}
                  {selectedSupplier.gstin && <div style={{ fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}>🔢 GSTIN: <span style={{ fontFamily: 'monospace' }}>{selectedSupplier.gstin}</span></div>}
                  {(selectedSupplier.city || selectedSupplier.state) && <div style={{ fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}>🏙️ {[selectedSupplier.city, selectedSupplier.state].filter(Boolean).join(', ')}</div>}
                  {selectedSupplier.address && <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 6 }}>📍 {selectedSupplier.address}</div>}
                  {selectedSupplier.status && <div style={{ fontSize: 12, marginTop: 6 }}><span style={{ padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontSize: 11, background: selectedSupplier.status === 'active' ? '#dcfce7' : '#fef2f2', color: selectedSupplier.status === 'active' ? '#16a34a' : '#dc2626' }}>{selectedSupplier.status}</span></div>}
                  {selectedSupplier.notes && <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 6, fontStyle: 'italic' }}>{selectedSupplier.notes}</div>}
                  <button onClick={() => { setEditBill(null); setShowBillModal(true); }}
                    style={{ marginTop: 14, width: '100%', padding: '9px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                    📦 Record New Purchase Bill
                  </button>
                </div>

                {/* Detail tab bar */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  {[['bills', '📦 Bills'], ['ledger', '📒 Ledger']].map(([key, label]) => (
                    <button key={key} onClick={() => setSupplierDetailTab(key)}
                      style={{ padding: '6px 14px', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                        background: supplierDetailTab === key ? acc : 'transparent',
                        color: supplierDetailTab === key ? '#fff' : (darkMode ? '#94A3B8' : '#64748B') }}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Recent bills for this supplier */}
                {supplierDetailTab === 'bills' && (
                <div style={{ ...card, padding: 16, flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: darkMode ? '#F1F5F9' : '#0F172A', marginBottom: 10 }}>Recent Purchase Bills</div>
                  {billsForSupplier.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: darkMode ? '#64748B' : '#94A3B8', fontSize: 13 }}>No bills yet</div>
                  ) : billsForSupplier.slice(0, 8).map(b => (
                    <div key={b.id} style={{ padding: '10px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: darkMode ? '#F1F5F9' : '#0F172A' }}>{b.bill_number || '(No #)'}</div>
                        <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8' }}>{fmtDate(b.bill_date)} · {b.payment_mode}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 800, fontSize: 14, color: acc }}>₹{fmtNum(b.grand_total)}</div>
                        {parseFloat(b.balance_amount || 0) > 0 && (
                          <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>Due ₹{fmtNum(b.balance_amount)}</div>
                        )}
                        {statusBadge(b.payment_status)}
                      </div>
                    </div>
                  ))}
                </div>
                )}
                {/* Supplier Ledger */}
                {supplierDetailTab === 'ledger' && (
                  <div style={{ ...card, padding: 16, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: darkMode ? '#F1F5F9' : '#0F172A', marginBottom: 10 }}>📒 Ledger</div>
                    {supplierLedgerLoading ? (
                      <div style={{ textAlign: 'center', padding: '20px 0', color: darkMode ? '#64748B' : '#94A3B8', fontSize: 13 }}>Loading…</div>
                    ) : supplierLedger.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px 0', color: darkMode ? '#64748B' : '#94A3B8', fontSize: 13 }}>No ledger entries</div>
                    ) : (
                      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {supplierLedgerWithBalance.map((entry, idx) => (
                          <div key={entry.id || idx} style={{ padding: '8px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontSize: 12, color: darkMode ? '#F1F5F9' : '#0F172A', fontWeight: 600 }}>{entry.description || '—'}</div>
                              <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8' }}>
                                {entry.entry_date ? fmtDate(entry.entry_date) : entry.created_at ? fmtDate(entry.created_at) : '—'}
                                {' · '}{entry.payment_mode || '—'}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: entry.entry_type === 'payment_in' ? '#16A34A' : '#DC2626' }}>
                                {entry.entry_type === 'payment_in' ? '+' : '-'}₹{fmtNum(entry.amount)}
                              </div>
                              <div style={{ fontSize: 10, color: darkMode ? '#64748B' : '#94A3B8' }}>
                                Bal: ₹{fmtNum(Math.abs(entry.running_balance || 0))} {(entry.running_balance || 0) >= 0 ? 'Dr' : 'Cr'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Purchase Bills Tab ── */}
        {tab === 'bills' && (
          <div>
            <div style={{ ...card, padding: 12, marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input style={{ ...inp, flex: 1, minWidth: 180 }} placeholder="Search by supplier or bill #…" value={billSearch} onChange={e => setBillSearch(e.target.value)} />
              <select style={inp} value={billStatus} onChange={e => setBillStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="paid">✅ Paid</option>
                <option value="unpaid">⏳ Unpaid</option>
                <option value="partial">🔶 Partial</option>
              </select>
              <button onClick={() => { setEditBill(null); setEditSupplier(null); setShowBillModal(true); }}
                style={{ padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
                ➕ New Bill
              </button>
            </div>
            <div style={{ ...card, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                <thead>
                  <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                    {['Bill #', 'Supplier', 'Date', 'Items', 'Amount', 'Mode', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: h === '' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allBills.map(b => (
                    <tr key={b.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: acc }}>{b.bill_number || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: darkMode ? '#F1F5F9' : '#0F172A' }}>{b.supplier_name}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B', whiteSpace: 'nowrap' }}>{fmtDate(b.bill_date)}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>{b.items?.length || 0} items</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#F1F5F9' : '#0F172A', whiteSpace: 'nowrap' }}>
                        <div>₹{fmtNum(b.grand_total)}</div>
                        {parseFloat(b.balance_amount || 0) > 0 && (
                          <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>Due ₹{fmtNum(b.balance_amount)}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>{b.payment_mode}</td>
                      <td style={{ padding: '10px 12px' }}>{statusBadge(b.payment_status)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                        <button onClick={() => { setEditBill(b); setShowBillModal(true); setEditSupplier(suppliers.find(s => s.id === b.supplier_id) || { id: b.supplier_id, name: b.supplier_name }); }}
                          style={{ padding: '4px 10px', background: 'transparent', border: `1px solid ${acc}`, color: acc, borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Edit</button>
                      </td>
                    </tr>
                  ))}
                  {allBills.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 50, textAlign: 'center', color: darkMode ? '#64748B' : '#94A3B8' }}>No purchase bills found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Outstanding Tab ── */}
        {tab === 'outstanding' && (
          <div>
            <div style={{ ...card, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: darkMode ? '#F1F5F9' : '#0F172A' }}>
                Suppliers with outstanding balance — {outstanding.length} suppliers
              </div>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#dc2626' }}>
                Total: ₹{fmtNum(outstanding.reduce((s, r) => s + parseFloat(r.outstanding || 0), 0))}
              </div>
            </div>
            {outstanding.length === 0 ? (
              <div style={{ ...card, padding: 60, textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>🎉</div>
                <p style={{ color: darkMode ? '#64748B' : '#94A3B8' }}>No outstanding payments — all bills are paid!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {outstanding.map(s => (
                  <div key={s.id} style={{ ...card, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontWeight: 800, fontSize: 15, color: darkMode ? '#F1F5F9' : '#0F172A', marginBottom: 2 }}>{s.name}</div>
                      {s.phone && <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8' }}>📞 {s.phone}</div>}
                      {s.gstin && <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8', fontFamily: 'monospace' }}>GSTIN: {s.gstin}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626' }}>₹{fmtNum(s.outstanding)}</div>
                        <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8' }}>Outstanding</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>₹{fmtNum(s.total_purchased)}</div>
                        <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8' }}>Total Purchased</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {s.phone && <>
                          <a href={`tel:${s.phone}`} style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', borderRadius: 7, textDecoration: 'none', fontWeight: 700, fontSize: 12 }}>📞 Call</a>
                          <WhatsAppButton phone={s.phone} name={s.name} />
                        </>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showSupplierModal && (
        <SupplierModal
          supplier={editSupplier}
          onClose={() => { setShowSupplierModal(false); setEditSupplier(null); }}
          onSaved={() => { setShowSupplierModal(false); setEditSupplier(null); loadSuppliers(); refreshSuppliers(true); }}
        />
      )}

      {showBillModal && (
        <PurchaseBillModal
          supplier={editSupplier || selectedSupplier || null}
          suppliers={sharedSuppliers.length > 0 ? sharedSuppliers : suppliers}
          bill={editBill}
          onClose={() => {
            setShowBillModal(false); setEditBill(null); setEditSupplier(null);
            if (cameFromDash) { setCameFromDash(false); navigate('/dashboard'); }
          }}
          onSaved={() => {
            const fromDash = cameFromDash;
            setCameFromDash(false);
            setShowBillModal(false); setEditBill(null); setEditSupplier(null);
            if (selectedId) loadBillsForSupplier(selectedId);
            if (tab === 'bills') loadAllBills();
            loadSuppliers();
            loadOutstanding();
            refreshSuppliers(true);
            refreshProducts(true);
            if (fromDash) navigate('/dashboard');
          }}
        />
      )}
    </Layout>
  );
};

export default Suppliers;
